import { useEffect, useState } from 'react';
import { usePWA } from './usePWA';

interface PWAAnalytics {
  installPromptShown: number;
  installAccepted: number;
  installDismissed: number;
  visitCount: number;
  isInstalled: boolean;
  installDate: number | null;
  offlineUsage: number;
  gesturesUsed: number;
}

export const usePWAAnalytics = () => {
  const { isInstalled, isOnline } = usePWA();
  const [analytics, setAnalytics] = useState<PWAAnalytics>({
    installPromptShown: 0,
    installAccepted: 0,
    installDismissed: 0,
    visitCount: 0,
    isInstalled: false,
    installDate: null,
    offlineUsage: 0,
    gesturesUsed: 0
  });

  useEffect(() => {
    // Cargar analytics desde localStorage
    const loadedAnalytics: PWAAnalytics = {
      installPromptShown: parseInt(localStorage.getItem('pwa-prompt-shown') || '0'),
      installAccepted: parseInt(localStorage.getItem('pwa-install-accepted') || '0'),
      installDismissed: parseInt(localStorage.getItem('pwa-dismiss-count') || '0'),
      visitCount: parseInt(localStorage.getItem('pwa-visit-count') || '0'),
      isInstalled: localStorage.getItem('pwa-installed') === 'true',
      installDate: localStorage.getItem('pwa-install-date') 
        ? parseInt(localStorage.getItem('pwa-install-date')!)
        : null,
      offlineUsage: parseInt(localStorage.getItem('pwa-offline-usage') || '0'),
      gesturesUsed: parseInt(localStorage.getItem('pwa-gestures-used') || '0')
    };

    setAnalytics(loadedAnalytics);
  }, []);

  useEffect(() => {
    // Trackear uso offline
    if (!isOnline) {
      const count = parseInt(localStorage.getItem('pwa-offline-usage') || '0');
      localStorage.setItem('pwa-offline-usage', (count + 1).toString());
    }
  }, [isOnline]);

  const trackPromptShown = () => {
    const count = parseInt(localStorage.getItem('pwa-prompt-shown') || '0');
    localStorage.setItem('pwa-prompt-shown', (count + 1).toString());
  };

  const trackInstallAccepted = () => {
    localStorage.setItem('pwa-install-accepted', '1');
    localStorage.setItem('pwa-installed', 'true');
    localStorage.setItem('pwa-install-date', Date.now().toString());
  };

  const trackGestureUsed = (gestureName: string) => {
    const count = parseInt(localStorage.getItem('pwa-gestures-used') || '0');
    localStorage.setItem('pwa-gestures-used', (count + 1).toString());
    localStorage.setItem(`pwa-gesture-${gestureName}`, Date.now().toString());
  };

  const getConversionRate = () => {
    if (analytics.installPromptShown === 0) return 0;
    return (analytics.installAccepted / analytics.installPromptShown) * 100;
  };

  const getDaysSinceInstall = () => {
    if (!analytics.installDate) return null;
    const days = Math.floor((Date.now() - analytics.installDate) / (1000 * 60 * 60 * 24));
    return days;
  };

  return {
    analytics,
    trackPromptShown,
    trackInstallAccepted,
    trackGestureUsed,
    getConversionRate,
    getDaysSinceInstall
  };
};
